import type { Extensions, Editor as TipTapEditor } from "@tiptap/core";
import type { Ref } from "react";
import {
  EditorContent,
  type EditorContentHandle,
} from "@/components/ui/editor/rich-text";
import {
  type FieldBindingProps,
  PolyField,
  pickBinding,
} from "@/fields/_internal/poly-field";

type RichTextContentFieldBaseProps = {
  ref?: Ref<EditorContentHandle>;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  /** Applied to the inner `EditorContent`. */
  className?: string;
  emptyEditorClass?: string;
  /** Extra Tiptap extensions (mentions, keyboard shortcuts). Memoize at the call site — Tiptap builds its extension list once. */
  extensions?: Extensions;
  /** Pasted / dropped images. */
  onUploadImages?: (files: File[]) => void;
  /** Fired AFTER the value is committed — for draft caches. */
  onValueChange?: (value: string) => void;
  /** Extra blur side effect. `field.handleBlur()` is always called first. */
  onBlur?: (editor: TipTapEditor) => void;
  onFocus?: () => void;
};

export type RichTextContentFieldProps = RichTextContentFieldBaseProps &
  FieldBindingProps;

/**
 * Rich-text BINDING ONLY — no provider, no border box, no FieldWrapper, and no
 * error slot (the host owns those). Must be rendered inside an `<Editor>`
 * provider from `@/components/ui/editor/rich-text`.
 *
 * Cadence: rich text is the FREE-TYPING kind — `handleChange` on every content
 * change (including Tiptap's mount-`onChange`, which is harmless because it does
 * not blur), `handleBlur` on editor blur. That is the whole contract.
 *
 * Use `RichTextField` for a normal form body; use this when the surface owns its
 * own editor chrome (chat composers).
 */
export function RichTextContentField(props: RichTextContentFieldProps) {
  const {
    ref,
    placeholder,
    autoFocus,
    disabled,
    className,
    emptyEditorClass,
    extensions,
    onUploadImages,
    onValueChange,
    onBlur,
    onFocus,
  } = props;
  const binding = pickBinding(props);
  return (
    <PolyField<string> {...binding}>
      {(field) => (
        <EditorContent
          ref={ref}
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={disabled}
          className={className}
          emptyEditorClass={emptyEditorClass}
          extensions={extensions}
          onUploadImages={onUploadImages}
          defaultValue={field.state.value}
          onChange={(value: string) => {
            field.handleChange(value);
            onValueChange?.(value);
          }}
          onBlur={(editor) => {
            field.handleBlur();
            onBlur?.(editor);
          }}
          onFocus={onFocus}
        />
      )}
    </PolyField>
  );
}
